import { useMemo, useState, useEffect, useCallback } from "react";
import { TabbedInterface } from "../../../components/layout/tabbed-interface/TabbedInterface";
import { useTranslation } from "../../../hooks/useTranslation";
import ProductListWrapper from "./components/ProductListWrapper";
import { tabColumnMapping } from "./components/standartListData";
import { ActionsButton } from "./components/ActionsButton/ActionsButton";
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Container,
} from "@mui/material";
import { useInventoryCounts } from "./components/useInventoryCounts";
import { useCustom } from "@refinedev/core";
import { Search, Clear } from "@mui/icons-material";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useForm } from "react-hook-form";
import { SyncrosaleLoader } from "../../../components/layout/components/SyncrosaleLoader";

const ASIN_REGEX = /^[a-z0-9]{10}$/;

interface Product {
  marketplaceProduct?: {
    product: {
      productStatus?: string;
      productSubStatus?: string;
    };
    marketplaceProductStatus?: string;
    marketplaceProductSubStatus?: string;
  };
  storeProductStatus?: string;
  storeProductSubStatus?: string;
  outOfCriteriaReasons?: string[];
}

export const Inventory = () => {
  const { translate } = useTranslation("pages.inventoryManagement.inventory");
  const { counts, loading } = useInventoryCounts();
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { register, watch, reset } = useForm({
    defaultValues: { asin: "" },
  });

  const asinValue = watch("asin");

  const [product, setProduct] = useState<Product | null>(null);
  const [debouncedLostAsin, setDebouncedLostAsin] = useState("");
  const [localError, setLocalError] = useState(false);

  const { refetch: searchLostAsin, isFetching: isSearching } = useCustom({
    url: `store/${selectedCountry?.id}/product/${debouncedLostAsin.toUpperCase()}`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry && !!debouncedLostAsin,
      retry: false,

      onError: () => {
        setProduct(null);
        setLocalError(true);
        reset({ asin: asinValue });
      },

      onSuccess: (data) => {
        const productData = data?.data as Product | null;

        if (!productData) {
          setProduct(null);
          setLocalError(true);
          reset({ asin: asinValue });
          return;
        }

        setProduct(productData);
        setLocalError(false);
        reset({ asin: asinValue });
      },
    },
    errorNotification: () => false,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const normalized = asinValue.trim().toLowerCase();

      if (ASIN_REGEX.test(normalized)) {
        setDebouncedLostAsin(normalized);
        searchLostAsin();
      } else {
        setDebouncedLostAsin("");
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [asinValue, searchLostAsin]);

  const handleClearLostAsin = useCallback(() => {
    reset({ asin: "" });
    setDebouncedLostAsin("");
    setProduct(null);
    setLocalError(false);
  }, [reset]);

  const tabs = useMemo(
    () =>
      tabColumnMapping.map((tabColumnInfo) => ({
        label: `tabs.${tabColumnInfo.path}`,
        path: tabColumnInfo.path,
        component: <ProductListWrapper tabInfo={tabColumnInfo} />,
      })),
    [],
  );

  if (loading) return <SyncrosaleLoader />;

  return (
    <>
      <TabbedInterface
        tabs={tabs}
        defaultPath="active"
        translate={translate}
        editable
        editableLocationKey={"pages.inventoryManagement.inventory"}
        tabCounts={counts}
        headerActions={
          <Box display="flex" gap={1} alignItems="center">
            <ActionsButton />
          </Box>
        }
      />

      <Container maxWidth={"sm"} component={Paper} sx={{ mt: 2, p: 2 }}>
        <TextField
          {...register("asin")}
          placeholder="Kayıp ASIN arama"
          size="small"
          error={localError}
          sx={{ width: 250, mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Search fontSize="small" />
                  )}
                </InputAdornment>
              ),
              endAdornment: asinValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearLostAsin}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Sub Status</TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>product</TableCell>
                <TableCell>
                  {product?.marketplaceProduct?.product.productStatus || "-"}
                </TableCell>
                <TableCell>
                  {product?.marketplaceProduct?.product.productSubStatus || "-"}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>marketplace</TableCell>
                <TableCell>
                  {product?.marketplaceProduct?.marketplaceProductStatus || "-"}
                </TableCell>
                <TableCell>
                  {product?.marketplaceProduct?.marketplaceProductSubStatus ||
                    "-"}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>store</TableCell>
                <TableCell>{product?.storeProductStatus || "-"}</TableCell>
                <TableCell>{product?.storeProductSubStatus || "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={1}>
          <Typography variant="subtitle2" fontWeight="bold" color="error">
            Out of Criteria Reasons :
          </Typography>

          <Stack spacing={1} mt={0.5}>
            {product?.outOfCriteriaReasons?.map((reason: string) => (
              <Box
                key={reason}
                px={1}
                py={0.5}
                bgcolor="error.main"
                color="common.white"
                borderRadius={0.5}
                fontSize={12}
              >
                {reason}
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </>
  );
};
